import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { formatPence, fuzzyFilter } from '@hcd/domain'
import { FileSignature, LayoutGrid, List, Search, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { ContractCards, ContractList } from '@/components/contracts/views'
import { PageHeader } from '@/components/shell/page'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { contractsQuery, type ContractSummary } from '@/lib/contracts'
import { useTitle } from '@/lib/use-title'
import { cn } from '@/lib/utils'

const TABS = [
  ['live', 'Active', (c: ContractSummary) => c.status === 'active'],
  ['review', 'Review due', (c: ContractSummary) => c.reviewDue],
  ['notice', 'Notice given', (c: ContractSummary) => c.status === 'notice_given'],
  ['ended', 'Ended', (c: ContractSummary) => c.status === 'ended' || c.status === 'cancelled'],
  ['all', 'All', () => true],
] as const
type Tab = (typeof TABS)[number][0]

const VIEWS = [
  ['list', 'List', List],
  ['cards', 'Cards', LayoutGrid],
] as const
type View = (typeof VIEWS)[number][0]

const VIEW_KEY = 'hcd.contracts.view'
const storedView = (): View | undefined => {
  try {
    const v = localStorage.getItem(VIEW_KEY)
    return v === 'list' || v === 'cards' ? v : undefined
  } catch {
    return undefined
  }
}

export const Route = createFileRoute('/_app/contracts/')({
  validateSearch: z.object({
    show: z.enum(TABS.map((t) => t[0]) as [Tab, ...Tab[]]).optional(),
    view: z.enum(['list', 'cards']).optional(),
    q: z.string().max(100).optional(),
  }),
  component: ContractsPage,
})

function ContractsPage() {
  useTitle('Contracts')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view: View = search.view ?? storedView() ?? 'list'
  const show: Tab = search.show ?? 'live'
  const [text, setText] = useState(search.q ?? '')
  const { data, isPending, isError, error } = useQuery(contractsQuery())

  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== text)
        navigate({ search: (s) => ({ ...s, q: text || undefined }), replace: true })
    }, 200)
    return () => clearTimeout(t)
  }, [text, search.q, navigate])

  const setSearch = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true })
  const setView = (v: View) => {
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      /* private mode: fine */
    }
    setSearch({ view: v })
  }

  const all = data ?? []
  const base = useMemo(
    () =>
      fuzzyFilter(all, search.q ?? '', (c) => [
        c.reference,
        c.customerName,
        c.siteName,
        c.title,
        c.quoteReference,
        c.contractTypeName,
      ]),
    [all, search.q],
  )
  const rows = base.filter(TABS.find((t) => t[0] === show)![2])
  const live = all.filter((c) => c.status === 'active' || c.status === 'notice_given')
  const annual = live.reduce((n, c) => n + c.annualValuePence, 0)

  return (
    <>
      <PageHeader
        title="Contracts"
        description={
          live.length
            ? `${live.length} live ${live.length === 1 ? 'agreement' : 'agreements'} · ${formatPence(annual)} a year before VAT`
            : 'Signed client agreements appear here when a client accepts a quote.'
        }
      />

      <div className="mb-4 grid gap-3 rounded-xl border bg-card p-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            aria-label="Search contracts"
            placeholder="Search by client, site or reference"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="h-11 pl-9 text-base md:h-10 md:text-[15px]"
          />
        </div>
        <div
          role="radiogroup"
          aria-label="View"
          className="inline-flex w-full rounded-lg border bg-muted p-1 md:w-auto"
        >
          {VIEWS.map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={view === key}
              onClick={() => setView(key)}
              className={cn(
                'inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium md:flex-none',
                view === key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>

      <nav
        aria-label="Filter contracts"
        className="-mx-4 mb-4 overflow-x-auto px-4 sm:mx-0 sm:px-0"
      >
        <ul className="flex w-max gap-2">
          {TABS.map(([key, label, fn]) => {
            const count = base.filter(fn).length
            return (
              <li key={key}>
                <button
                  type="button"
                  aria-pressed={show === key}
                  onClick={() => setSearch({ show: key === 'live' ? undefined : key })}
                  className={cn(
                    'h-9 rounded-full border px-3.5 text-sm font-medium whitespace-nowrap',
                    show === key
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-card hover:bg-secondary',
                  )}
                >
                  {label}{' '}
                  <span
                    className={cn(
                      'tabular',
                      show === key ? 'text-primary-foreground/80' : 'text-muted-foreground',
                    )}
                  >
                    {count}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      {search.q && (
        <p
          className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
          aria-live="polite"
        >
          {rows.length} {rows.length === 1 ? 'contract matches' : 'contracts match'}
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            onClick={() => {
              setText('')
              setSearch({ q: undefined })
            }}
          >
            <X className="size-3.5" aria-hidden />
            Clear search
          </button>
        </p>
      )}

      {isError ? (
        <p role="alert" className="text-destructive">
          {error.message}
        </p>
      ) : isPending ? (
        <div className="grid gap-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : all.length === 0 ? (
        <Empty className="rounded-xl border bg-card py-12">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileSignature />
            </EmptyMedia>
            <EmptyTitle>No contracts yet</EmptyTitle>
            <EmptyDescription>
              When a client signs a proposal, their agreement is filed here automatically, with the
              signatures and evidence.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Link
              to="/quotes"
              search={{ show: 'client' }}
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              See proposals with clients
            </Link>
          </EmptyContent>
        </Empty>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border bg-card p-6 text-[15px] text-muted-foreground">
          {search.q
            ? 'Nothing matches your search. Try fewer or different words.'
            : show === 'review'
              ? 'No reviews are due in the next 60 days.'
              : 'No contracts here.'}
        </p>
      ) : view === 'cards' ? (
        <ContractCards rows={rows} />
      ) : (
        <ContractList rows={rows} />
      )}
    </>
  )
}
